// Predictive search through Shopify's Section Rendering API: the ns-predictive-search section renders each
// result with the same card state and tier price as the cards, so a pre-order never reads "In stock" at list price.

export function buildSuggestUrl(base, query, section = 'ns-predictive-search', limit = 4) {
  const params = new URLSearchParams({ q: query.trim() });
  params.set('resources[type]', 'product,article,query');
  params.set('resources[limit]', String(limit));
  params.set('resources[limit_scope]', 'each');
  params.set('resources[options][unavailable_products]', 'last');
  params.set('section_id', section);
  return `${base || '/search/suggest'}?${params.toString()}`;
}

export function sectionInner(html) {
  const open = html.indexOf('<div id="shopify-section-');
  if (open < 0) return '';
  const start = html.indexOf('>', open);
  const end = html.lastIndexOf('</div>');
  return end > start ? html.slice(start + 1, end).trim() : '';
}

// Before the shopper types (07 Interactive Prototype SEARCH overlay, motion audit fix 21): the chosen collection's first
// products in the same rows, rendered by the same section on the collection URL, fetched once when the dialog opens.
export const popularUrl = (base, section = 'ns-predictive-search') => `${base}${base.includes('?') ? '&' : '?'}section_id=${section}`;
export const showsPopular = query => String(query ?? '').trim().length < 2;

if (typeof customElements !== 'undefined' && !customElements.get('ns-search')) {
  customElements.define('ns-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-ns-search-results]');
      this.popular = this.querySelector('[data-ns-search-popular]');
      this.dialog = this.closest('dialog');
      this.onInput = () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(this.input.value), 200);
      };
      this.onOpen = () => {
        this.input?.focus();
        this.loadPopular();
      };
      this.input?.addEventListener('input', this.onInput);
      this.dialog?.addEventListener('ns:dialog-open', this.onOpen);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.input?.removeEventListener('input', this.onInput);
      this.dialog?.removeEventListener('ns:dialog-open', this.onOpen);
    }
    async loadPopular() {
      if (!this.popular || this.popularLoaded) return;
      this.popularLoaded = true;
      try {
        const response = await fetch(popularUrl(this.popular.dataset.nsSearchPopular, this.dataset.section));
        if (!response.ok) throw new Error(String(response.status));
        this.popular.innerHTML = sectionInner(await response.text());
        this.popular.hidden = !this.popular.children.length || !showsPopular(this.input?.value);
      } catch {
        this.popularLoaded = false;
      }
    }
    async search(query) {
      this.controller?.abort();
      if (this.popular) this.popular.hidden = !showsPopular(query) || !this.popular.children.length;
      if (query.trim().length < 2) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        return;
      }
      this.controller = new AbortController();
      try {
        const response = await fetch(buildSuggestUrl(this.dataset.url, query, this.dataset.section), { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        this.results.innerHTML = sectionInner(await response.text());
      } catch (error) {
        if (error.name === 'AbortError') return;
        const message = document.createElement('p');
        message.className = 'ns-search__empty';
        message.textContent = this.dataset.error || '';
        this.results.replaceChildren(message);
      }
      this.results.hidden = false;
    }
  });
}
